import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sparkles, Settings2, Info, Leaf, Weight, Euro, MapPin, Package, Sprout } from 'lucide-react';
import { imgUrl } from '@/lib/api';
import MatchExplainBatch from '@/components/MatchExplainBatch';
import BatchFavoriteButton from '@/components/BatchFavoriteButton';

const fmt = (n) => n != null ? new Intl.NumberFormat('it-IT').format(n) : '—';
const QUALITY_LABEL = {
  standard: 'Standard', biologico: 'Biologico', igp: 'IGP', dop: 'DOP', in_conversione: 'In conversione bio',
};
const QUALITY_COLOR = {
  biologico: 'bg-chartreuse text-obsidian',
  dop: 'bg-terracotta text-white',
  igp: 'bg-foreground text-background',
  standard: 'bg-secondary text-foreground',
  in_conversione: 'bg-secondary text-foreground',
};
const PACKAGING_LABEL = {
  sfuso: 'Sfuso', cassette: 'In cassette', bins: 'Bins', sacchi: 'In sacchi', contenitori_industriali: 'Contenitori industriali',
};

function BatchCard({ b }) {
  const img = b.images?.[0] ? imgUrl(b.images[0]) : null;
  return (
    <Link
      to={`/partite/${b.batch_id}`}
      data-testid={`sugg-batch-${b.batch_id}`}
      className="group block rounded-2xl overflow-hidden border border-border bg-card hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary/40">
        {img ? (
          <img src={img} alt={b.fruit_type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full grid place-items-center"><Sprout size={56} className="opacity-15" strokeWidth={1.2}/></div>
        )}
        <div className="absolute top-3 left-3">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${QUALITY_COLOR[b.quality_grade] || 'bg-background/90'}`}>
            {QUALITY_LABEL[b.quality_grade] || b.quality_grade}
          </span>
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {b.match_score != null && (
            <div className="text-xs font-bold px-3 py-1.5 rounded-full bg-chartreuse text-obsidian">
              {b.match_score}% match
            </div>
          )}
          <BatchFavoriteButton batchId={b.batch_id}/>
        </div>
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.fruit_type}</div>
        <h3 className="font-display text-xl leading-tight mt-1 line-clamp-1">{b.variety || b.fruit_type}</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground"><Weight size={13}/> <span>{fmt(b.quantity_quintals)} q</span></div>
          <div className="flex items-center gap-1.5 text-muted-foreground"><Package size={13}/> <span>{PACKAGING_LABEL[b.packaging] || '—'}</span></div>
        </div>
        {b.location?.comune && (
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12}/> {[b.location.comune, b.location.region].filter(Boolean).join(', ')}</div>
        )}
        <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo</div>
            <div className="font-display text-2xl">€ {fmt(b.price_per_quintal)}<span className="text-xs text-muted-foreground font-sans">/q</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale</div>
            <div className="text-sm font-medium">€ {fmt(b.total_price)}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function SuggestedBatches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPrefs, setHasPrefs] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/auth/me');
        const prefs = me.data?.preferences || {};
        const hasBatchPrefs = !!(prefs.batch_fruit_types?.length || prefs.batch_qualities?.length ||
                                 prefs.batch_price_min || prefs.batch_price_max ||
                                 prefs.batch_qty_min || prefs.batch_qty_max);
        setHasPrefs(hasBatchPrefs);
        const { data } = await api.get('/batches/suggested');
        setItems(data || []);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Suggerite per te</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter flex items-center gap-3">
            <Leaf size={38} strokeWidth={1.4} className="text-chartreuse"/> Partite compatibili
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl">
            Il punteggio è calcolato da regole trasparenti sulle tue preferenze (tipo prodotto, qualità, prezzo, quantità, regione).
          </p>
        </div>
        <Link to="/preferenze"><Button variant="outline" className="rounded-full"><Settings2 size={14} className="mr-2"/> Modifica preferenze</Button></Link>
      </div>

      {!hasPrefs && (
        <div className="rounded-2xl border border-dashed border-border p-8 mb-8 flex items-center gap-4 flex-wrap">
          <Info size={22} className="text-muted-foreground"/>
          <div>
            <div className="font-medium">Non hai ancora impostato preferenze per le partite.</div>
            <div className="text-sm text-muted-foreground">Vai in Preferenze e scegli tipo prodotto, qualità, budget e quantità che ti interessano.</div>
          </div>
          <Link to="/preferenze" className="ml-auto"><Button className="rounded-full">Imposta ora</Button></Link>
        </div>
      )}

      {loading ? <div className="text-sm text-muted-foreground">Caricamento…</div> :
        items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center">
            <Sparkles className="mx-auto text-muted-foreground" size={28} strokeWidth={1.4}/>
            <h3 className="mt-4 font-display text-2xl">Nessuna partita da suggerire ancora.</h3>
            <p className="mt-2 text-sm text-muted-foreground">Le nuove partite pubblicate appariranno qui ordinate per compatibilità.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {items.map(b => (
              <div key={b.batch_id}>
                <BatchCard b={b}/>
                {b.match_reasons?.length > 0 && (
                  <ul className="mt-3 text-xs space-y-1">
                    {b.match_reasons.slice(0,5).map((r,i) => {
                      const isPlus = r.startsWith('+');
                      const isMinus = r.startsWith('−') || r.startsWith('-');
                      return (
                        <li key={i} className={`${isPlus?'text-foreground':isMinus?'text-destructive':'text-muted-foreground'}`}>
                          {r}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <MatchExplainBatch batchId={b.batch_id}/>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
